import { useCallback, useSyncExternalStore } from 'react'
import { MOBILE_MEDIA_QUERY } from 'app/lib/breakpoints'

/**
 * Subscribes to a CSS media query. Uses useSyncExternalStore so the first
 * render already reflects the real match (no mount flash) and there is no
 * tearing between concurrent renders.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === `undefined` || !window.matchMedia) return () => {}
      const mq = window.matchMedia(query)
      mq.addEventListener(`change`, onStoreChange)
      return () => mq.removeEventListener(`change`, onStoreChange)
    },
    [query],
  )

  const getSnapshot = useCallback(
    () => (typeof window !== `undefined` && window.matchMedia ? window.matchMedia(query).matches : false),
    [query],
  )

  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => false, // server snapshot — SPA has no SSR, kept so the hook is total
  )
}

/**
 * True below the Tailwind `md` breakpoint (< 768px). The ONLY sanctioned JS
 * breakpoint check — use it in the shell and shared primitives, never for
 * page-level component selection. See docs/mobile-responsive-patterns.md.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_MEDIA_QUERY)
}
